import { useCallback, useEffect, useRef, useState } from 'react';
import { MoveLeftIcon } from 'lucide-react';
import { Button } from 'common/components';
import { useLocation, useNavigate } from 'react-router-dom';

import styles from './Terms.module.scss';

const NAV_ITEMS = [
    { id: 'agreement-to-terms', label: 'Agreement to Terms' },
    { id: 'eligibility-and-permitted-use', label: 'Eligibility and Permitted Use' },
    { id: 'purpose-of-data-submission', label: 'Purpose of Data Submission' },
    { id: 'consent-to-be-contacted', label: 'Consent to Be Contacted' },
    { id: 'intellectual-property', label: 'Intellectual Property' },
    { id: 'disclaimers', label: 'Disclaimers' },
    { id: 'limitation-of-liability', label: 'Limitation of Liability' },
    { id: 'indemnification', label: 'Indemnification' },
    { id: 'third-party-links', label: 'Third-Party Links' },
    { id: 'governing-law-and-venue', label: 'Governing Law and Venue' },
    { id: 'general-provisions', label: 'General Provisions' },
    { id: 'contact-us', label: 'Contact Us' },
] as const;

const SCROLL_OFFSET = 120;

export const Terms = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    const updateActiveSection = useCallback(() => {
        const container = contentRef.current;
        if (!container) return;
        const sections = container.querySelectorAll<HTMLElement>('h3[id]');
        let current: string | null = null;
        sections.forEach((el) => {
            const top = el.getBoundingClientRect().top;
            if (top <= SCROLL_OFFSET) current = el.id;
        });
        setActiveSectionId(current);
    }, []);

    useEffect(() => {
        updateActiveSection();
        const handler = () => updateActiveSection();
        window.addEventListener('scroll', handler, { passive: true });
        return () => window.removeEventListener('scroll', handler);
    }, [updateActiveSection]);

    const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
        e.preventDefault();
        setActiveSectionId(id);
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const navBackHandler = () => {
        if (searchParams.get('demo-request')) {
            navigate('/demo-request');
        } else {
            navigate('/login');
        }
    };

    return (
        <div>
            <div className={styles.header}>
                <div className={styles.container}>
                    <Button variant='link' size='md' onClick={navBackHandler} icon={MoveLeftIcon}>
                        Back
                    </Button>
                    <h1>SLAI</h1>
                    <h2 className='mt-sm'>Terms of Service</h2>
                    <p className='text-sm mt-xs'>Last Updated: March 1, 2025</p>
                </div>
            </div>
            <div className={styles.body}>
                <div className={styles.container}>
                    <div className='flex items-start gap-xl'>
                        <aside className={styles.sidebar}>
                            <ul>
                                {NAV_ITEMS.map((item) => (
                                    <li key={item.id}>
                                        <a
                                            href={`#${item.id}`}
                                            className={activeSectionId === item.id ? styles.active : undefined}
                                            onClick={(e) => handleNavClick(e, item.id)}
                                        >
                                            {item.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </aside>
                        <main className={styles.content} ref={contentRef}>
                            <h3 id='agreement-to-terms'>1. Agreement to Terms</h3>
                            <p className='font-semibold'>
                                By submitting your name and phone number through any form on this website ("Site"), you
                                agree to be bound by these Terms of Service ("Terms") with SLAI, LLC ("SLAI," "we,"
                                "us," or "our"), a Delaware corporation. These Terms govern your use of this Site and
                                any submission of personal information herein.
                            </p>
                            <p className='font-semibold'>
                                If you do not agree to these Terms, please do not submit your information through this
                                Site. SLAI reserves the right to update these Terms at any time. The most current
                                version will always be available on this Site, and your continued use of the Site
                                following any changes constitutes acceptance of the revised Terms
                            </p>

                            <h3 id='eligibility-and-permitted-use'>2. Eligibility and Permitted Use</h3>
                            <p className='font-semibold'>
                                This Site is intended solely for use by business representatives and individuals acting
                                on behalf of a lawful business entity. By submitting your information, you represent and
                                warrant that:
                            </p>
                            <ul>
                                <li>You are at least 18 years of age.</li>
                                <li>
                                    You are authorized to submit the information provided on behalf of yourself or your
                                    organization.
                                </li>
                                <li>All information you provide is accurate, current, and complete.</li>
                                <li>
                                    Your use of this Site complies with all applicable federal, state, and local laws
                                    and regulations, including all applicable telemarketing, privacy, and consumer
                                    protection laws.
                                </li>
                            </ul>
                            <p>
                                This Site is not intended for use by consumers acting in a personal or household
                                capacity. SLAI does not knowingly collect information from individuals under the age of
                                18.
                            </p>

                            <h3 id='purpose-of-data-submission'>3. Purpose of Data Submission</h3>
                            <p className='font-semibold'>
                                Information submitted through this Site is collected solely to allow SLAI to contact you
                                regarding its AI-powered voice agent platform and agentic automation services (the
                                "Services"). SLAI may use the contact information you provide to follow up on your
                                inquiry, consistent with the consent you provide under Section 4 below and in accordance
                                with SLAI's Privacy Policy
                            </p>
                            <p className='font-semibold'>
                                For the avoidance of doubt, SLAI is a provider of information technology and AI-powered
                                communications services. Any marketing or outreach program conducted using SLAI's
                                Services is developed and coordinated by SLAI's clients, not by SLAI in its capacity as
                                a technology provider
                            </p>

                            <h3 id='consent-to-be-contacted'>4. Consent to Be Contacted</h3>
                            <h4>4.1 Consent to Receive SMS Messages</h4>
                            <p className='font-semibold'>
                                By checking the SMS consent checkbox on our contact form, you expressly provide your
                                prior express written consent to receive text messages (SMS) from SLAI, LLC at the phone
                                number you provided, including messages sent using automated technology. These messages
                                may include information about SLAI's services, follow-up communications, and scheduling.
                            </p>
                            <ul>
                                <li>Message frequency varies.</li>
                                <li>Standard message and data rates may apply.</li>
                                <li>To opt out at any time, reply STOP to any message you receive from us.</li>
                                <li>For help, reply HELP or contact us at legal@slai.ai.</li>
                            </ul>
                            <p>
                                Consent to receive SMS messages is not a condition of purchasing any product or service
                                from SLAI. SLAI will not initiate SMS messages unless you have provided this affirmative
                                opt-in consent.
                            </p>
                            <h4>4.2 Consent to Receive AI Voice Agent Calls</h4>
                            <p>
                                By checking the AI voice agent consent checkbox on our contact form, you expressly
                                provide your prior express written consent to receive automated telephone calls made
                                using an artificial intelligence voice agent system operated by or on behalf of SLAI,
                                LLC at the phone number you provided.
                            </p>
                            <ul>
                                <li>
                                    These calls are conducted by an AI voice agent, not a live human representative. The
                                    AI voice agent will identify itself as an automated system at the start of the call.
                                </li>
                                <li>Calls may be recorded for quality assurance, compliance, and training purposes.</li>
                                <li>
                                    SLAI's AI voice agent outputs are generated by artificial intelligence and large
                                    language model technology. Outputs may not always be fully accurate and should not
                                    be relied upon without independent verification.
                                </li>
                                <li>
                                    To opt out of future AI voice agent calls, you may verbally request removal during
                                    the call or contact us at legal@slai.ai.
                                </li>
                            </ul>
                            <p>
                                Consent to receive AI voice agent calls is not a condition of purchasing any product or
                                service from SLAI. SLAI will not initiate AI voice agent calls unless you have provided
                                this affirmative opt-in consent.
                            </p>

                            <h3 id='intellectual-property'>5. Intellectual Property</h3>
                            <p className='font-semibold'>
                                All content on this Site, including text, graphics, logos, interface design, software,
                                and AI-related technology, is the property of SLAI, LLC and is protected by applicable
                                copyright, trademark, patent, trade secret, and other intellectual property laws. No
                                right, license, or interest in any SLAI intellectual property is granted to you by
                                virtue of accessing or using this Site.
                            </p>

                            <p className='font-semibold'>
                                You may not reproduce, copy, distribute, modify, or create derivative works from any
                                content on this Site without SLAI's prior written consent. Unauthorized commercial use
                                of SLAI's intellectual property is expressly prohibited.
                            </p>

                            <h3 id='disclaimers'>6. Disclaimers</h3>
                            <p className='font-semibold'>
                                THIS SITE AND ALL CONTENT HEREIN ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS
                                WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. SLAI EXPRESSLY DISCLAIMS ALL
                                WARRANTIES, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
                                PARTICULAR PURPOSE, ACCURACY, AND NON-INFRINGEMENT.
                            </p>
                            <p className='font-semibold'>
                                SLAI'S SERVICES UTILIZE ARTIFICIAL INTELLIGENCE AND LARGE LANGUAGE MODEL TECHNOLOGY,
                                WHICH ARE DEVELOPING TECHNOLOGIES. AI-GENERATED OUTPUTS MAY NOT ALWAYS BE ACCURATE AND
                                ARE PROVIDED WITHOUT WARRANTY. USERS SHOULD NOT RELY ON AI-GENERATED OUTPUTS WITHOUT
                                INDEPENDENTLY VERIFYING THEIR ACCURACY.
                            </p>

                            <h3 id='limitation-of-liability'>7. Limitation of Liability</h3>
                            <p className='font-semibold'>
                                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, SLAI, ITS AFFILIATES, OFFICERS,
                                DIRECTORS, EMPLOYEES, AGENTS, AND SUPPLIERS SHALL NOT BE LIABLE FOR ANY INDIRECT,
                                INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, INCLUDING BUT NOT
                                LIMITED TO LOSS OF PROFITS, LOSS OF REVENUE, LOSS OF DATA, OR COST OF SUBSTITUTE
                                SERVICES, ARISING OUT OF OR RELATED TO YOUR USE OF OR INABILITY TO USE THIS SITE OR ANY
                                SUBMISSION OF INFORMATION HEREIN.
                            </p>
                            <p className='font-semibold'>
                                In no event shall SLAI's total aggregate liability to you for any claims arising out of
                                or related to these Terms exceed one hundred dollars ($100.00)
                            </p>

                            <h3 id='indemnification'>8. Indemnification</h3>
                            <p className='font-semibold'>
                                You agree to defend, indemnify, and hold harmless SLAI and its affiliates, officers,
                                directors, employees, and agents from and against any claims, liabilities, damages,
                                losses, costs, and expenses (including reasonable attorneys' fees) arising out of or
                                related to: (a) your use of this Site in violation of these Terms or applicable law; (b)
                                any inaccurate, false, or misleading information you submit through this Site; or (c)
                                your violation of any third-party rights in connection with your use of this Site.
                            </p>

                            <h3 id='third-party-links'>9. Third-Party Links</h3>
                            <p className='font-semibold'>
                                This Site may contain links to third-party websites. SLAI is not responsible for the
                                content, accuracy, or privacy practices of any linked third-party site. The presence of
                                a link does not imply endorsement or sponsorship by SLAI. You access linked sites at
                                your own risk.
                            </p>

                            <h3 id='governing-law-and-venue'>10. Governing Law and Venue</h3>
                            <p className='font-semibold'>
                                These Terms shall be governed by and construed in accordance with the laws of the State
                                of Florida, without giving effect to its conflict of law principles. All disputes
                                arising under or related to these Terms shall be brought exclusively in the state or
                                federal courts located in Miami, Florida, and you hereby consent to the personal
                                jurisdiction of such courts
                            </p>

                            <h3 id='general-provisions'>11. General Provisions</h3>
                            <p className='font-semibold'>
                                If any provision of these Terms is found to be invalid or unenforceable, the remaining
                                provisions shall remain in full force and effect. SLAI's failure to enforce any right or
                                provision of these Terms shall not constitute a waiver of such right or provision. These
                                Terms, together with SLAI's Privacy Policy, constitute the entire agreement between you
                                and SLAI with respect to your use of this Site and supersede all prior agreements or
                                understandings relating thereto.
                            </p>

                            <h3 id='contact-us'>12. Contact Us</h3>
                            <p className='font-semibold'>
                                For questions about these Terms, to exercise any rights, or for legal inquiries, please
                                contact us at:
                            </p>
                            <p className='mt-lg font-semibold'>
                                SLAI, LLC | <a href='mailto:legal@slai.ai'>legal@slai.ai</a> |{' '}
                                <a href='https://www.slai.ai'>www.slai.ai</a>
                            </p>
                        </main>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Terms;
